// ============================================================
// @vaptmenu/analytics-react — instrumentação de produto das SPAs
//
// Encapsula o fornecedor (PostHog) atrás de um provider e um hook:
// nenhum app importa posthog-js diretamente (design.md D2, lint D18).
// O SDK entra por import dinâmico depois do primeiro paint (D10), a
// sanitização acontece em before_send (privacy.ts) e a ausência de
// chave torna tudo no-op (D11). Falha aqui nunca derruba o app —
// mesma postura do FeatureFlagsProvider.
// ============================================================

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import type { PostHog } from 'posthog-js';
import { INGEST_PATH, UI_HOST } from './config';
import { isDevEnvironment } from './env';
import { readOptOutPreference, writeOptOutPreference } from './consent';
import {
  buildBeforeSend,
  buildSessionRecordingOptions,
  LANDING_PAGEVIEWS_ONLY,
  type PrivacyProfile,
} from './privacy';
import type { AnalyticsEvent, TrackArgs } from './taxonomy';

export type {
  AnalyticsEvent,
  AnalyticsEventProps,
  TrackArgs,
  MenuCouponSource,
  PanelPromotionShape,
  PanelPrintTicketKind,
  PanelPrinterTransport,
  PanelPrinterWidth,
  PanelOrderChannel,
  PanelOrderCancellationReason,
  PanelOrderChangeRequestKind,
  PanelOrderCustomerPolicyPreset,
  PanelPhotoFallbackReason,
  PanelSubscriptionPlan,
  PanelWhatsappSignupMode,
  PanelWhatsappOnboardingFailureReason,
  PanelLegalDocument,
  PanelLegalAcceptanceContext,
  PanelConversationReason,
  PanelConversationReplyKind,
} from './taxonomy';
export type { PrivacyProfile } from './privacy';
export { DENIED_PROPERTY_KEYS } from './privacy';

// =============================================================================
//  Tipos públicos
// =============================================================================

/** Propriedades de pessoa — só têm efeito no perfil `authenticated-b2b` (D7). */
export interface PersonProps {
  email?: string;
  name?: string;
  role?: string;
  [key: string]: string | number | boolean | null | undefined;
}

export interface AnalyticsProviderConfig {
  /** Project API key. Ausente/vazia ⇒ provider no-op completo (D11). */
  apiKey?: string;
  /**
   * Superfície instrumentada. `landing` mede só pageview e pageleave, no
   * perfil `public-anonymous`, sem `track` (change landing-acquisition-tracking).
   */
  app: 'menu' | 'panel' | 'landing';
  /** `staging` | `production` | `local` — redundante com o projeto de propósito (D15). */
  environment?: string;
  /** SHA curto do build (VITE_APP_RELEASE), para atribuir exceções ao build (D13). */
  release?: string;
  tenantSlug?: string | null;
  tenantId?: string | null;
  /**
   * true quando o slug do tenant é o primeiro segmento do path (modo
   * path-based do cardápio): a normalização de URL o remove para produzir
   * o mesmo caminho canônico do modo por subdomínio (D16).
   */
  slugInPath?: boolean;
  privacyProfile: PrivacyProfile;
  /**
   * Sinal do app para forçar no-op (ex.: MSW ativo). Os nomes de env
   * variam por app (`VITE_ENABLE_MSW` no cardápio, `VITE_MSW` no painel);
   * quem lê a env é o app, no ponto de montagem — nunca o pacote.
   */
  disabled?: boolean;
}

export interface AnalyticsApi {
  track: <E extends AnalyticsEvent>(event: E, ...args: TrackArgs<E>) => void;
  identify: (id: string, props?: PersonProps) => void;
  reset: () => void;
  setTenant: (slug: string | null, extra?: { tenantId?: string | null }) => void;
  optOut: () => void;
  optIn: () => void;
  /** true quando a captura está configurada (chave presente, fora de dev, sem `disabled`). */
  isEnabled: boolean;
  /** Estado corrente da preferência de recusa — alimenta os controles de opt-out (D19). */
  isOptedOut: boolean;
}

// =============================================================================
//  Internals
// =============================================================================

type QueuedOp =
  | { kind: 'track'; event: AnalyticsEvent; props?: Record<string, unknown> }
  | { kind: 'identify'; id: string; props?: PersonProps }
  | { kind: 'reset' }
  | { kind: 'setTenant'; slug: string | null; tenantId: string | null };

/**
 * Teto da fila de operações anteriores à carga do SDK: se ele nunca
 * subir (bloqueio, rede), a fila para de crescer e descarta as mais
 * antigas — nunca lança (spec: "Fila respeita limite").
 */
const QUEUE_LIMIT = 50;

/** Fallback quando requestIdleCallback não existe (Safari, jsdom). */
const IDLE_FALLBACK_DELAY_MS = 200;

type LoadState = 'idle' | 'loading' | 'ready' | 'failed';

function noop(): void {
  // provider ausente ou desligado
}

const NOOP_API: AnalyticsApi = {
  track: noop,
  identify: noop,
  reset: noop,
  setTenant: noop,
  optOut: noop,
  optIn: noop,
  isEnabled: false,
  isOptedOut: false,
};

const AnalyticsContext = createContext<AnalyticsApi | null>(null);

function warn(message: string, error?: unknown): void {
  if (error !== undefined) {
    console.warn(`[analytics] ${message}`, error);
  } else {
    console.warn(`[analytics] ${message}`);
  }
}

// =============================================================================
//  Provider
// =============================================================================

export interface AnalyticsProviderProps {
  config: AnalyticsProviderConfig;
  children: ReactNode;
}

export function AnalyticsProvider({ config, children }: AnalyticsProviderProps) {
  const { apiKey, app, environment, release, privacyProfile, disabled } = config;

  const enabled = Boolean(apiKey) && !disabled && !isDevEnvironment();

  const clientRef = useRef<PostHog | null>(null);
  const loadStateRef = useRef<LoadState>('idle');
  const queueRef = useRef<QueuedOp[]>([]);
  const tenantRef = useRef<{ slug: string | null; id: string | null }>({
    slug: config.tenantSlug ?? null,
    id: config.tenantId ?? null,
  });
  const slugInPathRef = useRef<boolean>(Boolean(config.slugInPath));
  slugInPathRef.current = Boolean(config.slugInPath);

  const [optedOut, setOptedOutState] = useState<boolean>(() => readOptOutPreference());
  const optedOutRef = useRef(optedOut);

  const applyTenantGlobals = useCallback((ph: PostHog) => {
    const { slug, id } = tenantRef.current;
    if (slug) {
      ph.register({ tenant_slug: slug });
    } else {
      ph.unregister('tenant_slug');
    }
    if (id) {
      ph.register({ tenant_id: id });
    } else {
      ph.unregister('tenant_id');
    }
  }, []);

  const applyGlobals = useCallback(
    (ph: PostHog) => {
      // register() persiste em localStorage: reaplicar a cada boot garante
      // que uma sessão anterior (outro tenant em modo path-based, outro
      // release) não vaze para os eventos desta.
      ph.register({
        app,
        environment: environment ?? 'unknown',
        release: release ?? 'unknown',
        // Nesta geração do posthog-js não existe mais a opção de init
        // `disable_geoip`; o contrato atual é a propriedade especial
        // `$geoip_disable` em cada evento — registrada aqui como super
        // property. A captura de IP também é desligada na configuração
        // do projeto, obrigatória na região US (D7 / task 1.3).
        $geoip_disable: true,
      });
      applyTenantGlobals(ph);
    },
    [app, environment, release, applyTenantGlobals],
  );

  const sanitizeIdentifyProps = useCallback(
    (props?: PersonProps): PersonProps | undefined => {
      if (privacyProfile === 'public-anonymous') {
        if (props && Object.keys(props).length > 0) {
          warn(
            'perfil public-anonymous descarta propriedades de pessoa no identify — apenas o id opaco é enviado (D7)',
          );
        }
        return undefined;
      }
      return props;
    },
    [privacyProfile],
  );

  const runOp = useCallback(
    (ph: PostHog, op: QueuedOp) => {
      switch (op.kind) {
        case 'track':
          ph.capture(op.event, op.props);
          break;
        case 'identify':
          ph.identify(op.id, sanitizeIdentifyProps(op.props));
          break;
        case 'reset':
          ph.reset();
          tenantRef.current = { slug: null, id: null };
          applyGlobals(ph);
          break;
        case 'setTenant':
          tenantRef.current = { slug: op.slug, id: op.tenantId };
          applyTenantGlobals(ph);
          break;
      }
    },
    [sanitizeIdentifyProps, applyGlobals, applyTenantGlobals],
  );

  const enqueue = useCallback((op: QueuedOp) => {
    const queue = queueRef.current;
    queue.push(op);
    if (queue.length > QUEUE_LIMIT) {
      queue.splice(0, queue.length - QUEUE_LIMIT);
    }
  }, []);

  const loadSdk = useCallback(async () => {
    if (loadStateRef.current !== 'idle') return;
    loadStateRef.current = 'loading';
    try {
      const mod = await import('posthog-js');
      const posthog = mod.default;
      if (optedOutRef.current) {
        // Recusa manifestada entre o agendamento e a carga: não inicializa.
        loadStateRef.current = 'idle';
        return;
      }
      const instance =
        posthog.init(apiKey as string, {
          // Proxy same-origin servido pelo nginx do próprio app (D9).
          api_host: INGEST_PATH,
          ui_host: UI_HOST,
          // Eventos anônimos custam até 4x menos; o cardápio quase nunca
          // identifica e o painel identifica sempre (D3).
          person_profiles: 'identified_only',
          // Autocapture desligado nas duas superfícies: no painel, $el_text
          // capturaria o nome do consumidor final impresso nos cards (D15).
          autocapture: false,
          capture_pageview: 'history_change',
          capture_pageleave: true,
          capture_exceptions: true,
          // O default do SDK é true: o cookie iria para .vaptmenu.com.br e o
          // mesmo cliente viraria a mesma pessoa entre lojas diferentes e
          // entre cardápio e painel. false isola a identidade por origem (D4).
          cross_subdomain_cookie: false,
          // advanced_disable_feature_flags NÃO é usado: essa opção também
          // desliga surveys e session recording — a config de gravação chega
          // pela resposta do endpoint de flags (D12). Nenhuma flag de produto
          // vive no PostHog; a regra é organizacional.
          before_send: buildBeforeSend({
            profile: privacyProfile,
            getTenantSlug: () => tenantRef.current.slug,
            isSlugInPath: () => slugInPathRef.current,
          }),
          session_recording: buildSessionRecordingOptions(privacyProfile),
          ...(app === 'landing' ? LANDING_PAGEVIEWS_ONLY : {}),
        }) ?? posthog;

      clientRef.current = instance;
      applyGlobals(instance);
      loadStateRef.current = 'ready';

      // Drena a fila na ordem original de disparo (spec: "Eventos
      // anteriores à carga são preservados").
      const pending = queueRef.current;
      queueRef.current = [];
      for (const op of pending) {
        try {
          runOp(instance, op);
        } catch (error) {
          warn('falha ao drenar operação enfileirada', error);
        }
      }
    } catch (error) {
      loadStateRef.current = 'failed';
      warn('falha ao carregar o SDK; instrumentação inativa nesta sessão', error);
    }
  }, [apiKey, privacyProfile, applyGlobals, runOp]);

  useEffect(() => {
    if (!enabled || optedOut) return;
    if (loadStateRef.current !== 'idle') return;

    // Carga tardia (D10): depois do primeiro paint, em momento ocioso.
    let cancelled = false;
    const start = () => {
      if (!cancelled) void loadSdk();
    };

    const win = window as Window & {
      requestIdleCallback?: (cb: () => void) => number;
      cancelIdleCallback?: (handle: number) => void;
    };
    let idleHandle: number | undefined;
    let timerHandle: ReturnType<typeof setTimeout> | undefined;
    if (typeof win.requestIdleCallback === 'function') {
      idleHandle = win.requestIdleCallback(start);
    } else {
      timerHandle = setTimeout(start, IDLE_FALLBACK_DELAY_MS);
    }
    return () => {
      cancelled = true;
      if (idleHandle !== undefined) win.cancelIdleCallback?.(idleHandle);
      if (timerHandle !== undefined) clearTimeout(timerHandle);
    };
  }, [enabled, optedOut, loadSdk]);

  const track = useCallback(
    <E extends AnalyticsEvent>(event: E, ...args: TrackArgs<E>) => {
      if (!enabled || optedOutRef.current) return;
      const props = args[0] as Record<string, unknown> | undefined;
      try {
        const ph = clientRef.current;
        if (ph && loadStateRef.current === 'ready') {
          ph.capture(event, props);
        } else {
          enqueue({ kind: 'track', event, props });
        }
      } catch (error) {
        warn('track falhou', error);
      }
    },
    [enabled, enqueue],
  );

  const identify = useCallback(
    (id: string, props?: PersonProps) => {
      if (!enabled || optedOutRef.current || !id) return;
      try {
        const ph = clientRef.current;
        if (ph && loadStateRef.current === 'ready') {
          ph.identify(id, sanitizeIdentifyProps(props));
        } else {
          enqueue({ kind: 'identify', id, props });
        }
      } catch (error) {
        warn('identify falhou', error);
      }
    },
    [enabled, enqueue, sanitizeIdentifyProps],
  );

  const reset = useCallback(() => {
    if (!enabled) return;
    try {
      const ph = clientRef.current;
      if (ph && loadStateRef.current === 'ready') {
        runOp(ph, { kind: 'reset' });
      } else {
        enqueue({ kind: 'reset' });
      }
    } catch (error) {
      warn('reset falhou', error);
    }
  }, [enabled, enqueue, runOp]);

  const setTenant = useCallback(
    (slug: string | null, extra?: { tenantId?: string | null }) => {
      if (!enabled) return;
      const tenantId = extra?.tenantId ?? null;
      try {
        const ph = clientRef.current;
        if (ph && loadStateRef.current === 'ready') {
          runOp(ph, { kind: 'setTenant', slug, tenantId });
        } else {
          // Não atualiza tenantRef aqui: a fila preserva a ordem relativa
          // entre eventos e trocas de tenant disparados antes da carga.
          enqueue({ kind: 'setTenant', slug, tenantId });
        }
      } catch (error) {
        warn('setTenant falhou', error);
      }
    },
    [enabled, enqueue, runOp],
  );

  const optOut = useCallback(() => {
    writeOptOutPreference(true);
    optedOutRef.current = true;
    setOptedOutState(true);
    queueRef.current = [];
    try {
      clientRef.current?.opt_out_capturing();
    } catch (error) {
      warn('opt-out no SDK falhou (preferência local persistida mesmo assim)', error);
    }
  }, []);

  const optIn = useCallback(() => {
    writeOptOutPreference(false);
    optedOutRef.current = false;
    setOptedOutState(false);
    try {
      clientRef.current?.opt_in_capturing();
    } catch (error) {
      warn('opt-in no SDK falhou', error);
    }
    // Se o SDK nunca carregou (recusa vigente desde o boot), o efeito de
    // carga reexecuta ao mudar `optedOut` e agenda o import.
  }, []);

  const value = useMemo<AnalyticsApi>(
    () => ({
      track,
      identify,
      reset,
      setTenant,
      optOut,
      optIn,
      isEnabled: enabled,
      isOptedOut: optedOut,
    }),
    [track, identify, reset, setTenant, optOut, optIn, enabled, optedOut],
  );

  return <AnalyticsContext.Provider value={value}>{children}</AnalyticsContext.Provider>;
}

// =============================================================================
//  Hook
// =============================================================================

/**
 * Acesso à instrumentação. Fora do provider (ou com ele desligado),
 * devolve uma implementação inócua — nunca lança.
 */
export function useAnalytics(): AnalyticsApi {
  const ctx = useContext(AnalyticsContext);
  return ctx ?? NOOP_API;
}
